// -----------------------------------------------------------------------
// <copyright file="AboutCallout.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  Callout,
  DirectionalHint,
  FocusZone,
  FocusZoneDirection,
  FocusZoneTabbableElements,
  IBaseProps,
  Link,
  Stack,
} from '@fluentui/react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { IAppState } from '../../../web-legacy/models/reduxState';
import { appContext } from '../../models/getHelp/appContext';
import { aboutCallOutStackStyles } from '../../self-help/styles/selfHelpStyles';

import { getString } from '../../utils/stringLoader';

import { StyledText } from './StyledText';

export interface IAboutCalloutProps extends IBaseProps, React.HTMLAttributes<HTMLElement> {
  targetId: string;
  onDismiss: (ev?: Event | React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => void;
}

export const AboutCallout = (props: IAboutCalloutProps): JSX.Element => {
  const appVersionString = useSelector<IAppState, string>(state => state.GlobalState.appVer);

  return (
    <Callout
      role="dialog"
      gapSpace={0}
      target={`#${props.targetId}`}
      directionalHint={DirectionalHint.bottomRightEdge}
      directionalHintFixed={true}
      onDismiss={props.onDismiss}
      setInitialFocus={true}
      isBeakVisible={false}
    >
      <FocusZone
        direction={FocusZoneDirection.vertical}
        handleTabKey={FocusZoneTabbableElements.all}
        isCircularNavigation={true}
      >
        <Stack styles={aboutCallOutStackStyles} tokens={{ childrenGap: '14px' }}>
          <Stack>
            <StyledText block>
              {getString('AboutCallout_AppName')} {appVersionString ?? `10.${appContext.appver}.0`}
            </StyledText>
            <StyledText block>{getString('AboutCallout_Copyright')}</StyledText>
          </Stack>
          <Link href="https://go.microsoft.com/fwlink/?LinkID=529064">{getString('AboutCallout_LicenseTerms')}</Link>
          <Link href="https://go.microsoft.com/fwlink/?LinkID=822631">
            {getString('AboutCallout_ServiceAgreement')}
          </Link>
          <Link href="https://go.microsoft.com/fwlink/?LinkID=521839">
            {getString('AboutCallout_PrivacyStatement')}
          </Link>
        </Stack>
      </FocusZone>
    </Callout>
  );
};
